import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Radio, Lock, AlertCircle } from 'lucide-react';

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    } else {
      // Redirect to admin dashboard on successful login
      navigate('/admin');
    }
    setLoading(false);
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-[#0F1115] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#D4AF37]/10 mb-4">
            <Radio size={32} className="text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            Christovibes Admin
          </h1>
          <p className="text-[#94A3B8] text-sm mt-2">Sign in to manage your radio station</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#171A21] rounded-2xl border border-white/5 p-6 sm:p-8 space-y-5">
          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-400 text-sm">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          
          <div>
            <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@christovibes.com"
              className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 transition-all"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 transition-all"
              required
            />
          </div>

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] transition-colors rounded-full px-6 py-3.5 shadow-lg shadow-[#D4AF37]/20 flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
          >
            <Lock size={18} />
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
